import React, { useState, useEffect } from 'react';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { PhoneCall } from 'lucide-react';

/**
 * Call Recordings component — works for both leads and students.
 * Pass either `leadId` or `phone` prop.
 */
export function CallRecordings({ leadId, phone }) {
    const [calls, setCalls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [playingId, setPlayingId] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const API_URL = process.env.REACT_APP_BACKEND_URL;

    useEffect(() => {
        if (!leadId && !phone) { setLoading(false); return; }
        setLoading(true);
        const endpoint = leadId
            ? `/leads/${leadId}/calls`
            : `/calls/by-phone?phone=${encodeURIComponent(phone)}`;
        apiClient.get(endpoint)
            .then(r => setCalls(r.data || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [leadId, phone]);

    const handlePlay = (call) => {
        if (!call.recording_id) return;
        if (playingId === call.id) {
            setPlayingId(null);
            setAudioUrl(null);
            return;
        }
        const token = localStorage.getItem('access_token');
        setAudioUrl(`${API_URL}/api/calls/${call.recording_id}/audio?token=${token}`);
        setPlayingId(call.id);
    };

    if (loading) return <div className="mt-4 text-xs text-muted-foreground">Loading calls...</div>;

    return (
        <div className="mt-4" data-testid="call-recordings-section">
            <div className="flex items-center gap-2 mb-2">
                <PhoneCall className="h-4 w-4 text-primary" />
                <Label className="text-sm font-medium">Call Recordings</Label>
                <Badge variant="secondary" className="text-[10px]">{calls.length}</Badge>
            </div>
            {audioUrl && (
                <div className="mb-2 p-2 rounded-lg bg-primary/5 border border-primary/20">
                    <audio
                        src={audioUrl}
                        controls
                        autoPlay
                        onEnded={() => { setPlayingId(null); setAudioUrl(null); }}
                        onError={() => { toast.error('Failed to play recording'); setPlayingId(null); setAudioUrl(null); }}
                        className="w-full h-8"
                        style={{ minHeight: '32px' }}
                        data-testid="audio-player"
                    />
                </div>
            )}
            <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                {calls.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-3 text-center">No 3CX calls found</p>
                ) : calls.map(call => (
                    <div key={call.id} className="flex items-center gap-2 p-2 rounded-md border bg-card text-xs hover:bg-muted/50 transition-colors">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${call.direction === 'Inbound' ? 'bg-blue-500/15 text-blue-500' : 'bg-emerald-500/15 text-emerald-500'}`}>
                            <PhoneCall className="h-3 w-3" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                                <span className="font-medium truncate">{call.agent_name || 'Unknown'}</span>
                                <span className="text-muted-foreground">{call.direction === 'Inbound' ? 'IN' : 'OUT'}</span>
                            </div>
                            <span className="text-muted-foreground">{call.call_date ? new Date(call.call_date).toLocaleDateString('en-AE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                        </div>
                        <span className="text-muted-foreground flex-shrink-0">{call.duration_display}</span>
                        {call.has_recording ? (
                            <Button size="sm" variant={playingId === call.id ? 'default' : 'ghost'} className="h-7 w-7 p-0 flex-shrink-0" onClick={() => handlePlay(call)}>
                                {playingId === call.id ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                                )}
                            </Button>
                        ) : (
                            <span className="text-muted-foreground/50 flex-shrink-0 text-[10px]">No audio</span>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
